import React from 'react';
import { ContextCode } from './ContextCode';

interface QuestionBooleanProps {
    question: {
        id: string;
        type: string;
        prompt: string;
        content: {
            context_code?: string;
        };
    };
    value: boolean | string | number | null;
    onChange: (value: boolean) => void;
    disabled?: boolean;
}

export const QuestionBoolean: React.FC<QuestionBooleanProps> = ({
    question,
    value,
    onChange,
    disabled = false
}) => {
    const { content } = question;

    // Normalize value to handle boolean, string, and number forms
    // SQLite might store true/false as 1/0
    const normalizedValue = value === true || value === 'true' || value === 1 ? true :
        value === false || value === 'false' || value === 0 ? false : null;

    return (
        <div className="space-y-6">
            {/* Context code block if present */}
            {content.context_code && <ContextCode code={content.context_code} />}

            {/* True/False buttons */}
            <div className="flex gap-4 justify-center">
                <button
                    type="button"
                    onClick={() => !disabled && onChange(true)}
                    disabled={disabled}
                    className={`
                        flex-1 max-w-xs py-4 px-8 rounded-xl font-semibold text-lg
                        transition-all duration-200 border-2
                        ${normalizedValue === true
                            ? 'bg-blue-600 text-white border-blue-700 shadow-lg shadow-blue-500/30'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                        }
                        ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
                    `}
                >
                    <div className="flex items-center justify-center gap-3">
                        <span className="text-2xl">✓</span>
                        <span>Vrai</span>
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => !disabled && onChange(false)}
                    disabled={disabled}
                    className={`
                        flex-1 max-w-xs py-4 px-8 rounded-xl font-semibold text-lg
                        transition-all duration-200 border-2
                        ${normalizedValue === false
                            ? 'bg-blue-600 text-white border-blue-700 shadow-lg shadow-blue-500/30'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                        }
                        ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
                    `}
                >
                    <div className="flex items-center justify-center gap-3">
                        <span className="text-2xl">✗</span>
                        <span>Faux</span>
                    </div>
                </button>
            </div>

            {/* Selection indicator */}
            {normalizedValue !== null && (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                    Votre sélection : <span className="font-semibold text-blue-700 dark:text-blue-300">
                        {normalizedValue ? 'Vrai' : 'Faux'}
                    </span>
                </div>
            )}
        </div>
    );
};

export default QuestionBoolean;
